import { TM } from "@litres-libs/tracker-manager";
import {
  hasWindow,
  getFromLocalStorage,
  saveToLocalStorage,
} from "@litres/utils";
import type React from "react";
import type { TSearchAllItem } from "@litres-libs/types";
import { SearchResponseItemTypesEnum } from "@litres-libs/types";
import type { ILastSearch } from "./types";
import { isSearchArt } from "./types";

/**
 * Функция которая принимает формат товара с бэка на англйиском и возвращает значение на русском.
 *
 * @param {string} itemType - Исходная строка
 * @return {string} Строка на русском
 *
 * @example
 *
 *     formatItemType("author")
 *     // "Автор"
 */
export const formatItemType = (
  itemType: SearchResponseItemTypesEnum
): string => {
  switch (itemType) {
    case SearchResponseItemTypesEnum.person:
      return "Автор";

    case SearchResponseItemTypesEnum.collection:
      return "Подборка";

    case SearchResponseItemTypesEnum.tag:
      return "Тег";

    case SearchResponseItemTypesEnum.publisher:
      return "Издательство";

    case SearchResponseItemTypesEnum.genre:
      return "Жанр";

    case SearchResponseItemTypesEnum.series:
      return "Серия";

    default:
      return "Ошибка";
  }
};

/**
 * Функция которая принимает строку с ценной и преобразует ее к корректному виду: обрезает нули для целых чисел, для цен с копейками меняет точку на запятую.
 *
 * @param {string} price - Строка с ценой
 * @return {string} Строка с ценой без нулей после точки или цена с копейками, но точка меняется на запятую
 *
 * @example
 *
 *	formatPrice("1423.00) // 1423
 *
 *  formatPrice("1423.23) // 1423,23
 *
 *  formatPrice("1423.2) // 1423,20
 *
 */
export const formatPrice = (price: number | undefined): string | undefined => {
  if (typeof price === "number" && !Number.isNaN(price)) {
    let fractionDigits = 0;
    if (price % 1 !== 0) {
      fractionDigits = 2;
    }
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "RUB",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(price);
  }
  return undefined;
};

export const getSearchPlaceholder = (
  width: number | null,
  t: (s: string) => string
): string => {
  if (width === null) {
    return "";
  }
  const t0 = t("Найти");
  const t1 = t("Книга");
  const t2 = t("серия");
  const t3 = t("автор");
  const t4 = t("жанр");
  const t5 = t("тег");
  const t6 = t("издательство");

  if (width > 502) {
    return [t1, t2, t3, t4, t6].join(", ");
  }

  if (width > 394 && width < 503) {
    return [t1, t2, t3, t4, t5].join(", ");
  }

  if (width > 354 && width < 395) {
    return [t1, t2, t3, t4].join(", ");
  }

  if (width > 297 && width < 355) {
    return [t1, t2, t3].join(", ");
  }

  if (width > 228 && width < 298) {
    return [t1, t2].join(", ");
  }

  if (width > 176 && width < 229) {
    return t1;
  }

  if (width < 177) {
    return t0;
  }

  return "";
};

export const highlightSearchQuery = (
  searchQuery: string,
  title: string
): string =>
  title.replace(
    new RegExp(searchQuery, "gi"),
    str => `<strong>${str}</strong>`
  );

/**
 * https://en.wikibooks.org/wiki/Algorithm_Implementation/Strings/Levenshtein_distance#JavaScript
 * Расстояние Левенштейна — метрика, измеряющая по модулю разность между двумя последовательностями символов.
 */
export const getEditDistance = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];

  // increment along the first column of each row
  let i = 0;
  for (; i <= b.length; i++) {
    matrix[i] = [i];
  }

  // increment each column in the first row
  let j = 0;
  for (; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (i = 1; i <= b.length; i++) {
    for (j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) == a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          Math.min(
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1
          )
        ); // deletion
      }
    }
  }

  return matrix[b.length][a.length];
};

/**
 * https://en.wikibooks.org/wiki/Algorithm_Implementation/Strings/Longest_common_substring#JavaScript
 * Наибольшая общая подстрока — подстрока двух или более строк, имеющая максимальную длину.
 */
export const longestCommonSubstring = (a: string, b: string): number => {
  // init max value
  let longestCommonSubstring = 0;
  // init 2D array with 0
  const table: [number[]] = [[]];
  const len1 = a.length;
  const len2 = b.length;
  let row = 0;
  let col = 0;
  for (row = 0; row <= len1; row++) {
    table[row] = [];
    for (col = 0; col <= len2; col++) {
      table[row][col] = 0;
    }
  }
  // fill table
  let i = 0;
  let j = 0;
  for (i = 0; i < len1; i++) {
    for (j = 0; j < len2; j++) {
      if (a[i] === b[j]) {
        if (table[i][j] === 0) {
          table[i + 1][j + 1] = 1;
        } else {
          table[i + 1][j + 1] = table[i][j] + 1;
        }
        if (table[i + 1][j + 1] > longestCommonSubstring) {
          longestCommonSubstring = table[i + 1][j + 1];
        }
      } else {
        table[i + 1][j + 1] = 0;
      }
    }
  }
  return longestCommonSubstring;
};

/**
 * Получить результаты последнего поиска.гыук
 * UPD: Теперь вместо массива строк возвращается объект с полями value и server
 * Поле value содержит как раз поисковый запрос, а поле source содержит источник, откуда мы полчили этот поисковый запрос
 * "server" - если мы запросили историю посика с сервера, и "client" - если юзер недавно самостоятельно вбил
 */
export const getLastSearchResults = (): ILastSearch[] => {
  const lastSearchResults = getFromLocalStorage<ILastSearch[] | null>(
    "last_search"
  );

  return lastSearchResults && Array.isArray(lastSearchResults)
    ? lastSearchResults
    : [];
};

/**
 * Сохранит массив с объектами поисковых запросов в localStorage
 */
export const saveLastSearchResultsToLocalStorage = (
  lastSearchResults: ILastSearch[]
): void => {
  saveToLocalStorage({
    last_search: lastSearchResults,
  });
};

export const trackerSearchClickInput = (path: string): void => {
  TM.sendEvent(["LITRES_TRACKER"], {
    eventType: "search_click_input",
    params: {
      source_internal: path,
    },
  });
};

export interface ISpotlightClickData {
  search_id: string;
  query: string;
  row_number: number;
  id: number | string;
  type: string;
}
export interface ISpotlightClickHistory {
  search_id: string;
  query: string | undefined;
}

export const getType = (item: TSearchAllItem): string => {
  interface ITypes {
    [key: string]: string;
  }

  const artTypes: ITypes = {
    text: "text_book",
    audio: "audiobook",
    podcast: "podcast",
    podcast_part: "podcast_episode",
  };

  const searchTypes: ITypes = {
    author: "person",
    collection: "collection",
    tag: "tag",
    art: "art",
    copyright_page: "publisher",
    genre: "genre",
    serie: "series",
  };

  if (isSearchArt(item)) {
    return artTypes[item.type];
  } else {
    return searchTypes[item.type];
  }
};

export const trackerSpotlightClickItem = (
  eventData: ISpotlightClickData,
  e: React.MouseEvent,
  url: string | null,
  id?: number
): void => {
  e.preventDefault();
  const el = (e.target as HTMLElement).closest(".buttonWrapper");
  if (el?.className !== "buttonWrapper") {
    const data = id ? { ...eventData, id } : eventData;
    TM.sendEvent(["LITRES_TRACKER"], {
      eventType: "search_click_spotlight_item",
      params: {
        ...data,
      },
    });
  }
  if (hasWindow) {
    window.location.href = url || "#";
  }
};

export const trackerSpotlightClickButton = (
  eventData: ISpotlightClickData,
  is_purchase_flag: boolean
): void => {
  TM.sendEvent(["LITRES_TRACKER"], {
    eventType: "search_click_spotlight_button",
    params: {
      search_id: eventData.search_id,
      query: eventData.query,
      row_number: eventData.row_number,
      book_id: eventData.id,
      is_purchase_flag,
    },
  });
};

export const trackerSpotlightClickHistoryItem = (value: string): void => {
  TM.sendEvent(["LITRES_TRACKER"], {
    eventType: "search_click_history_item",
    params: {
      item: value,
    },
  });
};

export const formatNumber = (number: number): string => {
  return new Intl.NumberFormat("ru-RU").format(number);
};

export const getDividedNumber = (numberOfBooks: string): string => {
  const parts = numberOfBooks.split(" ");
  const count = formatNumber(Number(parts[0]));
  return count + " " + parts[1];
};
